import {SubscriptionTier, Token} from "@devour/client";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {useGetSubscriptionTiers} from "@/hooks/useGetSubscriptionTiers";

interface CurrentSubscriptionTier {
    /** The resolved tier config (Scout / Operator / Legend), or null for free users. */
    currentTier: SubscriptionTier | null;
    /** True when a tier is present (active or paused). */
    isSubscribed: boolean;
    /** True when the tier is present but its renewal payment is failing/retrying. */
    isPaused: boolean;
    /**
     * True when the subscription program is actually live — at least one tier
     * has an active pricing option (the same "purchasable" signal the
     * registration page uses). When false, there is nothing to sell, so the
     * account menu drops the tier/Free label and the Get Access upsell.
     */
    hasActiveTiers: boolean;
}

/*
 * Reads the user's persisted DevourPlay Access tier — the backend is the source
 * of truth (`user.subscriptionTier` + `subscriptionTierStatus`, written by the
 * Stripe/ownership webhooks), so the client never derives it from Stripe. The
 * stored id is resolved to its config from the cached tiers list. Shared by the
 * top-nav avatar (amber ring) and the account dropdown (Access header / upsell).
 */
export function useGetCurrentSubscriptionTier(fullToken: Token): CurrentSubscriptionTier {
    const {data: userProfile} = useGetUserProfile(fullToken);
    const {data: tiersRes} = useGetSubscriptionTiers(fullToken, 100, 0, "");

    const tierId = userProfile?.user?.subscriptionTier;
    /*
     * Compare as a string rather than importing the generated SubscriptionTierStatus
     * enum VALUE: the enum's runtime export can lag a client regen (Vite pre-bundle),
     * which would throw `Cannot read 'PAUSED' of undefined`. The stored value IS the
     * string "PAUSED"/"ACTIVE", so a string compare is both correct and regen-proof.
     */
    const isPaused = String(userProfile?.user?.subscriptionTierStatus) === "PAUSED";
    const currentTier = tierId
        ? tiersRes?.subscriptionTiers.find(t => t.id === tierId) ?? null
        : null;

    /*
     * A tier counts as purchasable only when the TIER is active and it has an
     * active price. The price check alone was wrong: admins receive inactive
     * tiers from the endpoint, and every unlaunched tier carried live Stripe
     * prices, so the Get Access upsell showed for a program that was not live.
     */
    const hasActiveTiers = (tiersRes?.subscriptionTiers ?? []).some(t => t.isActive && t.pricing?.some(p => p.isActive));

    return {
        currentTier,
        isSubscribed: Boolean(currentTier),
        isPaused,
        hasActiveTiers,
    };
}
